import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  TextInput,
  ActionIcon,
  Box,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { Plus, Trash2 } from 'react-feather';
import { cohortsApi, type DateConstraintUpdate } from '../../../services/api/cohorts';
import type { CohortWithDetails } from '../../../types';
import { parseDateOnly, toDateValue, toISODateString } from '../../../utils/dateHelpers';
import classes from './EditModals.module.css';

interface EditDateConstraintsModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

interface ConstraintRow {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  label: string;
}

// Generate unique ID for new rows
const generateId = () => Math.random().toString(36).substring(7);

export function EditDateConstraintsModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditDateConstraintsModalProps) {
  const [saving, setSaving] = useState(false);
  const [constraints, setConstraints] = useState<ConstraintRow[]>([]);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      if (cohort.date_constraints && cohort.date_constraints.length > 0) {
        setConstraints(
          cohort.date_constraints.map(c => ({
            id: c.id,
            startDate: parseDateOnly(c.start_date),
            endDate: parseDateOnly(c.end_date),
            label: c.label || '',
          }))
        );
      } else {
        // Start with one empty row
        setConstraints([{ id: generateId(), startDate: null, endDate: null, label: '' }]);
      }
    }
  }, [opened, cohort]);

  const addConstraint = () => {
    setConstraints(prev => [...prev, { id: generateId(), startDate: null, endDate: null, label: '' }]);
  };

  const removeConstraint = (id: string) => {
    setConstraints(prev => prev.filter(c => c.id !== id));
  };

  const updateConstraint = (id: string, field: keyof ConstraintRow, value: Date | null | string) => {
    setConstraints(prev =>
      prev.map(c => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const handleSave = async () => {
    // Filter out empty constraints
    const validConstraints = constraints.filter(c => c.startDate && c.endDate);

    try {
      setSaving(true);
      const data: DateConstraintUpdate[] = validConstraints.map(c => ({
        start_date: toISODateString(c.startDate)!,
        end_date: toISODateString(c.endDate)!,
        label: c.label || undefined,
      }));
      const updated = await cohortsApi.updateDateConstraints(cohort.id, data);
      onUpdate(updated);
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Date constraints updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update date constraints',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Date Constraints"
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Add date ranges when sessions cannot be scheduled (holidays, company events, etc.)
        </Text>

        {constraints.map((constraint, index) => (
          <Box key={constraint.id} className={classes.constraintRow}>
            <DateInput
              label={index === 0 ? 'Start Date' : undefined}
              placeholder="Start date"
              valueFormat="MM/DD/YYYY"
              value={constraint.startDate}
              onChange={(date) => updateConstraint(constraint.id, 'startDate', toDateValue(date))}
              clearable
            />
            <DateInput
              label={index === 0 ? 'End Date' : undefined}
              placeholder="End date"
              valueFormat="MM/DD/YYYY"
              value={constraint.endDate}
              onChange={(date) => updateConstraint(constraint.id, 'endDate', toDateValue(date))}
              minDate={constraint.startDate || undefined}
              clearable
            />
            <TextInput
              label={index === 0 ? 'Label (optional)' : undefined}
              placeholder="e.g., Holiday Break"
              value={constraint.label}
              onChange={(e) => updateConstraint(constraint.id, 'label', e.currentTarget.value)}
            />
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => removeConstraint(constraint.id)}
              disabled={constraints.length === 1}
              mt={index === 0 ? 24 : 0}
            >
              <Trash2 size={18} />
            </ActionIcon>
          </Box>
        ))}

        <Button
          variant="light"
          leftSection={<Plus size={16} />}
          onClick={addConstraint}
          w="fit-content"
        >
          Add Constraint
        </Button>

        <Group justify="flex-end" className="modal-footer">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditDateConstraintsModal;
